<script setup lang="ts">
import { computed } from 'vue'
import { CheckboxGroup } from '@/shared/ui/CheckboxGroup'
import { RadioGroup, Radio } from '@/shared/ui/RadioGroup'

/**
 * The days of the week, modelled as `number[]` of 0–6 with **Sunday as 0**, which is what
 * the backend counts from. `TimeslotsCheckboxes` is the same idea over the hours of a day,
 * and the two pair up in a schedule.
 *
 * `single` turns it into a radio group and still emits an array, so the form field keeps
 * one shape whichever way the screen asks.
 */
const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
]

const props = withDefaults(
  defineProps<{
    modelValue?: number[]
    /** Pick exactly one day; the value is still emitted as an array */
    single?: boolean
    disabled?: boolean
  }>(),
  { modelValue: () => [] }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number[]): void
}>()

const selected = computed(() => props.modelValue ?? [])
const singleValue = computed(() => selected.value[0] ?? null)

function onGroupUpdate(value: Array<string | number>) {
  emit(
    'update:modelValue',
    value.map((item) => Number(item))
  )
}

function onSingleUpdate(value: string | number | boolean | undefined) {
  emit('update:modelValue', [Number(value)])
}
</script>

<template>
  <RadioGroup
    v-if="single"
    class="weekdays-checkboxes__radios"
    :model-value="singleValue"
    :disabled="disabled"
    @update:model-value="onSingleUpdate"
  >
    <Radio
      v-for="option in WEEKDAY_OPTIONS"
      :key="option.value"
      :value="option.value"
      :disabled="disabled"
    >
      {{ option.label }}
    </Radio>
  </RadioGroup>

  <CheckboxGroup
    v-else
    :model-value="selected"
    :options="WEEKDAY_OPTIONS"
    :disabled="disabled"
    :columns="4"
    @update:model-value="onGroupUpdate"
  />
</template>

<style lang="scss" scoped>
.weekdays-checkboxes__radios {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px 16px;
}

@media (max-width: $bp-narrow) {
  .weekdays-checkboxes__radios {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
